import React, { useState, useEffect } from "react";
import "./scorebox.css";
import "./SentenceOrdering.css";
import InfoTooltip from "../../../InfoTooltip";
import TipButton from "../../../TipButton";
import StudentInfoTooltip from "../../../StudentInfoTooltip";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLeftRight } from "@fortawesome/free-solid-svg-icons";
import { handleScoreChange } from "./helpers/scoreHelpers";
import CategoryTags from "./CategoryTags";
const SentenceOrdering = ({
  sentences = [],
  correctAnswers = [],
  onChange,
  border,
  score,
  questionId,
  question = "",
  isStudentView = false,
  studentResponse,
  tip,
  questionCategories = [],
  showCategories = true,
  onFlip,
}) => {
  const [orderedSentences, setOrderedSentences] = useState([]);
  const [isCorrect, setIsCorrect] = useState(null);
  const [fullSentence, setFullSentence] = useState(question);
  const [draggedIndex, setDraggedIndex] = useState(null);

  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  useEffect(() => {
    if (Array.isArray(sentences) && sentences.length > 0) {
      if (isStudentView && !studentResponse) {
        setOrderedSentences(shuffleArray(sentences));
      } else {
        setOrderedSentences(sentences);
      }
    }
  }, [sentences, isStudentView, studentResponse]);

  useEffect(() => {
    if (
      isStudentView &&
      studentResponse &&
      Array.isArray(studentResponse.answer)
    ) {
      setOrderedSentences(studentResponse.answer);
    }
  }, [isStudentView, studentResponse]);

  useEffect(() => {
    //intentional effect to shuffle the words when the question is changed and pieces to appearwhen created by AI
    if (question && question.trim() !== "") {
      const splitSentences = question
        .replace(/\//g, " ") // Replace '/' with spaces
        .split(/\s+/)
        .filter((word) => word.trim() !== "");

      const shuffledSentences = shuffleArray(splitSentences);
      setOrderedSentences(shuffledSentences);
      setFullSentence(question);

      if (!isStudentView) {
        onChange({
          question: question,
          sentences: shuffledSentences,
          correctAnswers: splitSentences,
          questionId,
          questionCategories: ["grammar", "syntax"],
        });
      }
    }
  }, [question]);

  const handleDragStart = (e, index) => {
    e.dataTransfer.setData("text/plain", index);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    if (draggedIndex === null) return;

    const sourceIndex = draggedIndex;
    const clientX = e.clientX;
    const children = [...e.currentTarget.children];
    let targetIndex = children.findIndex((child) => {
      const rect = child.getBoundingClientRect();
      return clientX < rect.left + rect.width / 2;
    });

    if (targetIndex === -1) {
      targetIndex = children.length;
    }

    if (sourceIndex < targetIndex) {
      targetIndex--;
    }

    if (targetIndex === sourceIndex) return;

    const items = [...orderedSentences];
    const [movedItem] = items.splice(sourceIndex, 1);
    items.splice(targetIndex, 0, movedItem);

    setOrderedSentences(items);
    setDraggedIndex(targetIndex);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (isStudentView) {
      onChange({
        type: "SentenceOrdering",
        answer: orderedSentences,
      });
    } else {
      onChange({ sentences: orderedSentences, questionId });
    }
  };

  const handleFullSentenceChange = (e) => {
    const newSentence = e.target.value;
    setFullSentence(newSentence);

    const splitSentences = newSentence
      .replace(/\//g, " ") // Replace '/' with spaces
      .split(/\s+/)
      .filter((word) => word.trim() !== "");

    const shuffledSentences = shuffleArray(splitSentences);
    setOrderedSentences(shuffledSentences);
    onChange({
      question: newSentence,
      sentences: shuffledSentences,
      correctAnswers: splitSentences,
      questionId,
      questionCategories: ["grammar", "syntax"],
    });
  };

  const onScoreChange = handleScoreChange(onChange);

  const checkOrder = () => {
    const isCorrectOrder =
      JSON.stringify(orderedSentences) === JSON.stringify(correctAnswers);
    setIsCorrect(isCorrectOrder);
  };

  useEffect(() => {
    let timeoutId;
    if (isCorrect !== null) {
      timeoutId = setTimeout(() => {
        setIsCorrect(null);
      }, 5000);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isCorrect]);

  const shuffleSentences = () => {
    const shuffled = [...orderedSentences].sort(() => Math.random() - 0.5);
    setOrderedSentences(shuffled);
    onChange({ sentences: shuffled, questionId });
  };

  return (
    <div className={`question-container ${border}`}>
      <div className="question-header">
        {isStudentView ? (
          <>
            <div className="audio-recorder-question">
              Drag and drop the pieces below in the correct order to make a
              meaningful sentence.
            </div>
            <StudentInfoTooltip type="Sentence Ordering" />
          </>
        ) : (
          <>
            <input
              type="text"
              value={fullSentence}
              onChange={handleFullSentenceChange}
              placeholder="Enter the full sentence here"
              className="question-input"
            />
            <InfoTooltip type="Sentence Ordering" />
          </>
        )}
      </div>
      <div
        className="sentence-pieces-container"
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {orderedSentences.map((sentence, index) => (
          <div
            key={index}
            draggable
            onDragStart={(e) => handleDragStart(e, index)}
            onDragEnd={handleDragEnd}
            className={`sentence-piece ${
              draggedIndex === index ? "dragging" : ""
            }`}
          >
            {sentence}
          </div>
        ))}
      </div>
      <div className="category-tags-container">
        <CategoryTags
          categories={questionCategories}
          onChange={(newCategories) =>
            onChange({
              questionCategories: newCategories,
            })
          }
          isStudentView={isStudentView}
          showCategories={showCategories}
        />
      </div>
      <div className="question-footer">
        <div className="question-footer-flex">
          <div className="button-wrapper">
            {!isStudentView && (
              <>
                <button className="question-footer-button" onClick={checkOrder}>
                  Check
                </button>
                <button
                  className="question-footer-button"
                  onClick={shuffleSentences}
                >
                  Shuffle
                </button>
              </>
            )}
          </div>
          <TipButton
            tip={tip}
            onChange={onChange}
            isStudentView={isStudentView}
            onFlip={onFlip}
          />
          <div className="scorebox-container">
            <label htmlFor="score">Score:</label>
            <input
              type="number"
              id="score"
              value={score}
              onChange={onScoreChange}
              min="0"
              step="1"
              readOnly={isStudentView}
              className={isStudentView ? "student-view" : ""}
            />
          </div>
        </div>
      </div>
      {!isStudentView && isCorrect !== null && (
        <p>{isCorrect ? "Correct order!" : "Incorrect order. Try again!"}</p>
      )}
    </div>
  );
};

export default SentenceOrdering;
